/**
 * foldText — diacritic + Nordic-digraph folding for search matching.
 *
 * Maps "Gravhøj", "gravhoj", "Ålborg", "Aalborg", "Æbelholt" and
 * "aebelholt" onto a single ASCII form so the unified search matches
 * regardless of how the user spells Nordic letters. Matching only: the
 * folded string is never displayed.
 *
 * Consumers: hooks/useSearch.ts folds both query and record fields.
 * Dependency-free and hot (runs over tens of thousands of records per
 * keystroke), so all regexes are module-level constants.
 */

// Combining marks left behind by NFD decomposition (a-ring, o-umlaut, e-acute...).
const COMBINING_MARKS = /[̀-ͯ]/g;

// Letters that do NOT decompose under NFD and need an explicit mapping.
// o-slash and the Icelandic thorn/eth are the important ones here.
const NON_DECOMPOSING = /[øæåöäþðß]/g;
const NON_DECOMPOSING_MAP: Record<string, string> = {
  "ø": "o",
  "æ": "ae",
  "å": "a",
  "ö": "o",
  "ä": "a",
  "þ": "th",
  "ð": "d",
  "ß": "ss",
};

// ASCII digraph spellings people type instead of the accented letter.
// Applied AFTER the mappings above so the fold is symmetric:
//   "æ" -> "ae" -> "a"   and a typed "ae" -> "a"      (Æbelholt == aebelholt)
//   "ø" -> "o"           and a typed "oe" -> "o"      (Østerby == Oesterby)
//   "å" -> "a"           and a typed "aa" -> "a"      (Ålborg == Aalborg)
// Order matters within the alternation too: "oe" and "ae" are collapsed in
// the same pass as "aa" because none of them can produce another digraph.
const DIGRAPHS = /oe|ae|aa/g;
const DIGRAPH_MAP: Record<string, string> = { oe: "o", ae: "a", aa: "a" };

const WHITESPACE = /\s+/g;

/**
 * Fold a string to its diacritic-free, digraph-collapsed lowercase form.
 * Returns "" for empty/whitespace-only input.
 */
export function foldText(s: string): string {
  if (!s) return "";
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .replace(NON_DECOMPOSING, (c) => NON_DECOMPOSING_MAP[c])
    .replace(DIGRAPHS, (d) => DIGRAPH_MAP[d])
    .replace(WHITESPACE, " ")
    .trim();
}
